/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react"
import { createBrowserRouter, RouterProvider, useLocation } from "react-router-dom"
import * as React from "react"
import { lazy, useState, Suspense, useEffect } from "react"
import { Skeleton } from "antd"
import { UserPermissionConsumer, UserPermissionContext } from "@curefit/commons-rc-rbac"
import { RBACUtils } from "../utils/rbacUtils"
import { WATCHMEN_CONTEXT, WATCHMEN_RESOURCES } from "../constants"
import { isEmpty } from "lodash"
import { GlobalError, NotFound } from "../components/Error/Error"
import { ROUTE_NAMES } from "./helper"
import ResumePage from "../containers/ResumePage"

const Calender = lazy(() => import("../containers/Calendar/Calendar"))
const Homepage = lazy(() => import("../containers/Homepage"))
const PatientList = lazy(() => import("../containers/PatientList/PatientList"))
const DoctorConsultList = lazy(() => import("../containers/DoctorConsultList/DoctorConsultList"))
const GlpApprovalQueue = lazy(() => import("../containers/GlpApprovalQueue/GlpApprovalQueue"))
const PatientPage = lazy(() => import("../containers/PatientPage/PatientPage"))
const Profile = lazy(() => import("../containers/patient/Profile/Profile"))
const Consultations = lazy(() => import("../containers/patient/Consultations/Consultations"))
const PsychologistConsultations = lazy(() => import("../containers/patient/PsychologistConsultations/PsychologistConsultations"))
const ConsultationDetails = lazy(() => import("../containers/patient/ConsultationDetails/ConsultationDetails"))
const Roster = lazy(() => import("../containers/Roster/Roster"))
const AutomatedWorkingHour = lazy(() => import("../containers/AutomatedWorkingHour/AutomatedWorkingHour"))
const OngoingDoctorConsult = lazy(() => import("../containers/patient/OngoingDoctorConsult/OngoingDoctorConsult"))
const OngoingCoachConsultation = lazy(() => import("../containers/patient/OngoingCoachConsultation/OngoingCoachConsultation"))
const EditDoctorConsult = lazy(() => import("../containers/patient/EditDoctorConsult/EditDoctorConsult"))
const HistoryView = lazy(() => import("../containers/patient/HistoryView/HistoryView"))
const TreatmentPlan = lazy(() => import("../containers/TreatmentPlan"))
const TreatmentPlanEditor = lazy(() => import("../containers/TreatmentPlan/TreatmentPlanEditor"))
const AdherenceDashboard = lazy(() => import("../containers/patient/AdherenceDashboard/AdherenceDashboard"))
const OdinWrapper = lazy(() => import("../containers/Odin/OdinWrapper"))
const PatientNotesContainer = lazy(() => import("../containers/PatientNotesContainer/PatientNotesContainer"))
const DailyActivities = lazy(() => import("../containers/DailyActivities/DailyActivities"))
const CgmData = lazy(() => import("../containers/CgmData/CgmData"))
const PatientVitals = lazy(() => import("../containers/PatientVitals/PatientVitals"))
const PatientGoals = lazy(() => import("../containers/PatientGoals/PatientGoals"))
const LabTests = lazy(() => import("../containers/LabTests/LabTests"))
const SugarJournal = lazy(() => import("../containers/SugarJournal/SugarJournal"))
const PatientDocuments = lazy(() => import("../containers/PatientDocuments/PatientDocuments"))
const FitnessData = lazy(() => import("../containers/FitnessData/FitnessData"))
const ActivePlan = lazy(() => import("../containers/patient/ActivePlan/ActivePlan"))
const PatientRecallView = lazy(() => import("../containers/PatientRecallView/PatientRecallView"))
const CreatePatientNotes = lazy(() => import("../containers/CreatePatientNotes/CreatePatientNotes"))
const ViewPatientNotes = lazy(() => import("../containers/ViewPatientNotes/ViewPatientNotes"))
const DoctorProfile = lazy(() => import("../containers/DoctorProfile/DoctorProfile"))
const LinksLibrary = lazy(() => import("../containers/LinksLibrary/LinksLibrary"))
const Requests = lazy(() => import("../containers/Requests/Requests"))
const Catalogue = lazy(() => import("../containers/MedicineCatalogue/Catalogue"))
const RCAPatientList = lazy(() => import("../containers/noProgressRCA/RCAPatientList"))
const NoProgressRCAContainer = lazy(() => import("../containers/noProgressRCA/NoProgressRCAContainer"))
const MyPlanner = lazy(() => import("../containers/MyPlanner/MyPlanner"))
const MealPlanDashboard = lazy(() => import("../containers/MealPlanDashboard/MealPlanDashboard"))
const PollsContainer = lazy(() => import("../containers/polls/PollsContainer"))
const PollCreator = lazy(() => import("../containers/polls/PollCreator"))
const PollDetailPage = lazy(() => import("../containers/polls/PollDetailPage"))
const ChallengesContainer = lazy(() => import("../containers/challenges/ChallengesContainer"))
const CreateChallenge = lazy(() => import("../containers/challenges/CreateChallenge"))
const ChallengeDetails = lazy(() => import("../containers/challenges/ChallengeDetails"))
const ExperimentsContainer = lazy(() => import("../containers/Experiments/Experiments"))
const ExperimentsViewWrapper = lazy(() => import("../containers/Experiments/ExperimentsViewWrapper/ExperimentsViewWrapper"))
const ExperimentDetails = lazy(() => import("../containers/Experiments/ExperimentDetails/ExperimentDetails"))
const ConsultRating = lazy(() => import("../containers/ConsultRating/ConsultRating"))
const Glp1ProgramContainer = lazy(() => import("../containers/GLP1ProgramContainer/GLP1ProgramContainer"))
const VerifyCoachConsultAccess = lazy(() => import("../containers/patient/consultation/VerifyCoachConsultAccess/VerifyCoachConsultAccess"))
interface IRoutesProps {}
const Routes = (props: IRoutesProps) => {
	const [watchmanContext, setWatchmanContext] = useState<string>(WATCHMEN_CONTEXT.ALL)

	//@ts-ignore
	const { userPermissions } = React.useContext(UserPermissionContext)

	useEffect(() => {
		if (!isEmpty(userPermissions)) {
			const keys = Object.keys(userPermissions)
			const selectedContext = keys[0]
			setWatchmanContext(selectedContext)
		}
	}, [userPermissions])

	const router = (userPermissions: any) =>
		createBrowserRouter([
			{
				path: ROUTE_NAMES.HOMEPAGE,
				element: <Homepage />,
				errorElement: <GlobalError />,
				children: [
					{
						index: true,
						element: <Calender userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.CALENDAR)} />,
					},
					{
						path: ROUTE_NAMES.PATIENTS,
						element: <PatientList userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_LIST)} />,
					},
					{
						path: ROUTE_NAMES.RESUME,
						element: <ResumePage />,
					},
					{
						path: ROUTE_NAMES.CONSULTS,
						element: <DoctorConsultList userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.DOCTOR_CONSULT_LIST)} />,
					},
					{
						path: ROUTE_NAMES.GLP_APPROVAL_QUEUE,
						element: <GlpApprovalQueue />,
					},

					{
						path: `${ROUTE_NAMES.PATIENT_DETAILS}/:userId/`,
						element: <PatientPage userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_LIST)} />,
						errorElement: <GlobalError />,
						children: [
							{
								index: true,
								element: <Profile userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_LIST)} />,
							},
							{
								path: ROUTE_NAMES.CONSULTATIONS,
								element: <Consultations userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_CONSULTATIONS)} />,
							},
							{
								path: ROUTE_NAMES.PSYCHOLOGIST_CONSULTATIONS,
								element: (
									<PsychologistConsultations
										userPermissions={userPermissions}
										permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PSYCHOLOGIST_CONSULTATIONS)}
									/>
								),
							},
							{
								path: `${ROUTE_NAMES.CONSULTATIONS}/:consultationId`,
								element: (
									<ConsultationDetails
										userPermissions={userPermissions}
										permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_CONSULTATIONS_DETAILS)}
									/>
								),
							},
							{
								path: `${ROUTE_NAMES.CONSULTATION}/:consultationId/${ROUTE_NAMES.ONGOING_DOCTOR_CONSULT}`,
								element: (
									<OngoingDoctorConsult userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.DOCTOR_ONGOING_CONSULT)} />
								),
							},
							{
								path: `${ROUTE_NAMES.VERIFY_COACH_CONSULT}`,
								element: <VerifyCoachConsultAccess />,
							},
							...[
								`${ROUTE_NAMES.CONSULTATION}/:consultationId/${ROUTE_NAMES.ONGOING_COACH_CONSULT}`,
								`${ROUTE_NAMES.CONSULTATION}/:consultationId/${ROUTE_NAMES.ONGOING_PSYCHOLOGIST_CONSULT}`,
							].map((path) => ({
								path: path,
								element: (
									<OngoingCoachConsultation userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.COACH_ONGOING_CONSULT)} />
								),
							})),
							{
								path: `${ROUTE_NAMES.CONSULTATION}/:consultationId/${ROUTE_NAMES.CONSULT_RATING}`,
								element: <ConsultRating userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.CONSULT_RATING)} />,
							},
							{
								path: `${ROUTE_NAMES.CONSULTATION}/:consultationId/${ROUTE_NAMES.EDIT_COACH_CONSULT}`,
								element: (
									<OngoingCoachConsultation userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.COACH_ONGOING_CONSULT)} />
								),
							},
							{
								path: `${ROUTE_NAMES.CONSULTATION}/:consultationId/${ROUTE_NAMES.EDIT_DOCTOR_CONSULT}`,
								element: <EditDoctorConsult userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.EDIT_DOCTOR_CONSULT)} />,
							},
							{
								path: `${ROUTE_NAMES.PATIENT_HISTORY}`,
								element: <HistoryView userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_HISTORY)} />,
							},

							{
								path: `${ROUTE_NAMES.ADHERENCE}`,
								element: <AdherenceDashboard userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_ADHERENCE)} />,
							},
							{
								path: ROUTE_NAMES.PATIENT_NOTES,
								element: <PatientNotesContainer userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_NOTES)} />,
								children: [
									{
										index: true,
										element: <ViewPatientNotes userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_NOTES)} />,
									},
									{
										path: `${ROUTE_NAMES.CREATE_PATIENT_NOTES}`,
										element: <CreatePatientNotes userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_NOTES)} />,
										errorElement: <GlobalError />,
									},
								],
							},
							{
								path: `${ROUTE_NAMES.DAILY_ACTIVITY}`,
								element: <DailyActivities userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_DAILY_ACT)} />,
							},
							{
								path: ROUTE_NAMES.CGM,
								element: <CgmData userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_CGM_ACT)} />,
							},
							{
								path: ROUTE_NAMES.VITALS,
								element: <PatientVitals userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_VITALS)} />,
							},
							{
								path: ROUTE_NAMES.GOALS,
								element: <PatientGoals userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_GOALS)} />,
							},
							{
								path: ROUTE_NAMES.EXPERIMENTS,
								element: <ExperimentsContainer userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.EXPERIMENTS)} />,
								children: [
									{
										index: true,
										element: <ExperimentsViewWrapper userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.EXPERIMENTS)} />,
									},
									{
										path: ROUTE_NAMES.EXPERIMENT_DETAILS,
										element: <ExperimentDetails userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.EXPERIMENTS)} />,
									},
								],
							},
							{
								path: ROUTE_NAMES.LAB_TEST,
								element: <LabTests userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_LAB_TEST)} />,
							},
							{
								path: ROUTE_NAMES.GLP_1_PROGRAM,
								element: <Glp1ProgramContainer userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_LAB_TEST)} />,
							},
							{
								path: ROUTE_NAMES.GLUCOSE,
								element: <SugarJournal userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_GLUCOSE)} />,
							},
							{
								path: ROUTE_NAMES.DOCUMENTS,
								element: <PatientDocuments userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_DOCUMENT)} />,
							},
							{
								path: ROUTE_NAMES.FITNESS_DATA,
								element: <FitnessData userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_FITNESS_DATA)} />,
							},
							{
								path: `${ROUTE_NAMES.ACTIVE_PLAN}`,
								element: <ActivePlan userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_ACTIVE_PLAN)} />,
							},
							{
								path: `${ROUTE_NAMES.LIFESTYLE_RECALL}`,
								element: <PatientRecallView userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.PATIENT_LIFESTYLE_RECALL)} />,
							},
							{
								path: "*",
								element: <NotFound />,
							},
						],
					},
					{
						path: ROUTE_NAMES.ROSTER,
						element: <Roster userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.ROSTER)} />,
					},
					{
						path: ROUTE_NAMES.REQUESTS,
						element: <Requests userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.REQUESTS)} />,
					},
					{
						path: ROUTE_NAMES.DOCTOR_PROFILE,
						element: <DoctorProfile userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.DOCTOR_PROFILE)} />,
					},
					{
						path: ROUTE_NAMES.AUTOMATED_ROSTER,
						element: <AutomatedWorkingHour userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.AUTOMATED_ROSTER)} />,
					},
					{
						path: ROUTE_NAMES.TREATMENT_PLAN,
						element: <TreatmentPlan userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.TREATMENT_PLAN)} />,
					},
					{
						path: ROUTE_NAMES.TREATMENT_PLAN + "/edit/:templateId",
						element: <TreatmentPlanEditor userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.TREATMENT_PLAN_EDITOR)} />,
					},
					{
						path: ROUTE_NAMES.TREATMENT_PLAN + "/add",
						element: <TreatmentPlanEditor userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.TREATMENT_PLAN_EDITOR)} />,
					},
					{
						path: ROUTE_NAMES.ODIN,
						element: <OdinWrapper userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.ODIN)} />,
					},
					{
						path: ROUTE_NAMES.LINKS_LIBRARY,
						element: <LinksLibrary />,
					},
					{
						path: ROUTE_NAMES.MEAL_PLAN,
						element: <MealPlanDashboard userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.MEAL_PLAN)} />,
					},
					{
						path: ROUTE_NAMES.RCA,
						element: <RCAPatientList userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.RCA)} />,
					},
					{
						path: ROUTE_NAMES.RCA_EDIT,
						element: <NoProgressRCAContainer userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.RCA_EDIT)} />,
					},
					{
						path: ROUTE_NAMES.TASK,
						element: <MyPlanner userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.TASK)} />,
					},
					{
						path: ROUTE_NAMES.CATALOGUE,
						element: <Catalogue userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.CATALOGUE)} />,
					},
					{
						path: `${ROUTE_NAMES.POLLS}`,
						element: <PollsContainer userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.POLLS)} />,
					},
					{
						path: `${ROUTE_NAMES.POLL_CREATE}`,
						element: <PollCreator userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.POLL_CREATE)} />,
					},
					{
						path: `${ROUTE_NAMES.POLL_DETAILS}`,
						element: <PollDetailPage userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.POLL_DETAILS)} />,
					},
					{
						path: `${ROUTE_NAMES.CHALLENGE}`,
						element: <ChallengesContainer userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.CHALLENGES)} />,
					},
					{
						path: `${ROUTE_NAMES.CHALLENGE_CREATE}`,
						element: <CreateChallenge userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.CHALLENGES_CREATE)} />,
					},
					{
						path: `${ROUTE_NAMES.CHALLENGE_DETAILS}`,
						element: <ChallengeDetails userPermissions={userPermissions} permission={RBACUtils.getReadPermission(watchmanContext, WATCHMEN_RESOURCES.CHALLENGES_DETAILS)} />,
					},
					{
						path: "*",
						element: <NotFound />,
					},
				],
			},
		])
	return (
        // @ts-ignore
        <UserPermissionConsumer>
            {({ userPermissions, isLoading }) => {
				if (isLoading) {
					return (
						<section
							id="permission-loading"
							css={{
								position: "relative",
								width: "100%",
								padding: "1em",
							}}
						>
							<Skeleton active />
						</section>
					)
				}
				return (
					<Suspense fallback={<Skeleton />}>
						<RouterProvider router={router(userPermissions)} />
					</Suspense>
				)
			}}
        </UserPermissionConsumer>
    );
}

export default Routes
